/* V2 Demo — premium healthcare UI inspired by superhealth.co.in
 * Standalone layout (no SiteLayout import) so this never touches the production design.
 * Desktop-first, no responsive optimisation (per client request). */

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Stethoscope, ShieldCheck, Activity, Users, Phone, MapPin, Mail,
  ArrowRight, ChevronDown, ChevronRight, Check, Star, Sparkles, Building2,
  HeartPulse, Pill, Syringe, FileText, Clock3, Award, BadgeCheck, Quote,
  Hospital, Calendar, MessageSquare, ArrowUpRight, ChevronUp,
} from "lucide-react";

/* ============================================================
 * Theme tokens (scoped to V2 only — won't leak into prod CSS)
 * ============================================================ */
const T = {
  bg: "#FFFFFF",
  bgSoft: "#F4F8F6",      // soft mint-tinted
  bgWash: "#EAF4F1",      // hero wash
  ink: "#0B2A2A",          // deep teal-ink
  inkSoft: "#516A6A",
  brand: "#0E7F76",        // primary teal
  brandDark: "#0A5C56",
  brandSoft: "#D6EBE7",
  accent: "#F5B945",       // warm gold accent
  line: "#E2EBE8",
};

/* ============================================================
 * V2 Layout — Header + Footer
 * ============================================================ */
const NAV = [
  ["Home", "/v2"],
  ["Plans", "/v2/plans"],
  ["Corporate", "/v2/corporate"],
  ["Benefits", "/v2/benefits"],
  ["About", "/v2/about"],
  ["Why us", "/v2/why-us"],
  ["FAQ", "/v2/faq"],
  ["Contact", "/v2/contact"],
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
      transition={{ delay }} variants={fadeUp} className={className}
    >{children}</motion.div>
  );
}

function V2Header() {
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#E2EBE8]" data-testid="v2-header">
      <div className="mx-auto max-w-7xl px-8 h-20 flex items-center justify-between">
        <Link to="/v2" className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center text-white font-bold text-lg" style={{ background: `linear-gradient(135deg, ${T.brand}, ${T.brandDark})` }}>N</div>
          <div className="leading-tight">
            <div className="font-display text-lg font-bold tracking-tight" style={{ color: T.ink }}>Nirmal Care</div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: T.inkSoft }}>Privilege Card · v2</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map(([label, href]) => {
            const active = pathname === href || (href !== "/v2" && pathname.startsWith(href));
            return (
              <Link key={href} to={href}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${active ? "bg-[#D6EBE7] text-[#0A5C56]" : "text-[#0B2A2A] hover:bg-[#F4F8F6]"}`}
                data-testid={`v2-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              >{label}</Link>
            );
          })}
        </nav>
        <div className="flex items-center gap-3">
          <a href="tel:+916357033301" className="hidden md:flex items-center gap-2 text-sm font-semibold" style={{ color: T.brand }}>
            <Phone className="h-4 w-4" /> +91 63570 33301
          </a>
          <Link to="/v2/plans" className="px-5 py-2.5 rounded-full text-white text-sm font-semibold flex items-center gap-2 shadow-[0_8px_24px_rgba(14,127,118,0.25)] hover:shadow-[0_12px_30px_rgba(14,127,118,0.35)] transition-all hover:-translate-y-0.5"
                style={{ background: `linear-gradient(135deg, ${T.brand}, ${T.brandDark})` }}
                data-testid="v2-cta-join">
            Join NPC <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function V2Footer() {
  return (
    <footer className="mt-32 bg-[#0B2A2A] text-white/85" data-testid="v2-footer">
      <div className="mx-auto max-w-7xl px-8 py-20 grid grid-cols-12 gap-10">
        <div className="col-span-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl grid place-items-center text-white font-bold" style={{ background: `linear-gradient(135deg, ${T.brand}, ${T.accent})` }}>N</div>
            <div className="font-display text-xl font-bold text-white">Nirmal Privilege Card</div>
          </div>
          <p className="mt-6 text-sm leading-relaxed text-white/65 max-w-sm">
            A premium healthcare privilege program at Nirmal Super Speciality Hospital, Adajan — designed for families who value timely, compassionate care.
          </p>
          <div className="mt-8 flex gap-3">
            {[Phone, Mail, MessageSquare].map((I, i) => (
              <a key={i} href="#" className="h-10 w-10 rounded-full border border-white/15 grid place-items-center hover:bg-white/10 transition-colors">
                <I className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
        {[
          ["Membership", [["Plans", "/v2/plans"], ["Benefits", "/v2/benefits"], ["Corporate", "/v2/corporate"], ["Why us", "/v2/why-us"]]],
          ["Hospital", [["About", "/v2/about"], ["Specialties", "#"], ["Doctors", "#"], ["Testimonials", "#"]]],
          ["Help", [["FAQ", "/v2/faq"], ["Contact", "/v2/contact"], ["Privacy", "/v2/privacy"], ["Terms", "/v2/terms"]]],
        ].map(([title, items]) => (
          <div key={title} className="col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-white/45 mb-5">{title}</div>
            <ul className="space-y-3 text-sm">
              {items.map(([label, href]) => (
                <li key={label}><Link to={href} className="hover:text-white transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
        ))}
        <div className="col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-white/45 mb-5">Contact</div>
          <p className="text-sm leading-relaxed text-white/65">
            Subhash Chandra Bose Marg,<br />Adajan, Surat — 394510<br /><br />
            <a href="tel:+916357033301" className="text-white/90 hover:text-white">+91 63570 33301</a>
          </p>
        </div>
      </div>
      <div className="border-t border-white/8">
        <div className="mx-auto max-w-7xl px-8 py-6 flex items-center justify-between text-xs text-white/45">
          <div>© 2026 Nirmal Super Speciality Hospital · NPC v2 design preview</div>
          <div>Made with care in Surat</div>
        </div>
      </div>
    </footer>
  );
}

function V2Layout({ children, demoNote }) {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif", color: T.ink }}>
      {demoNote && (
        <div className="bg-[#F5B945]/95 text-[#3B2A05] text-xs font-semibold py-2 text-center tracking-wide">
          🎨 V2 PREVIEW · This is a design demo on <code className="bg-black/10 px-1.5 py-0.5 rounded">/v2/*</code> · Production website is untouched
        </div>
      )}
      <V2Header />
      <main>{children}</main>
      <V2Footer />
    </div>
  );
}

/* ============================================================
 * Reusable primitives
 * ============================================================ */
const Tag = ({ children, color = T.brand }) => (
  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: `${color}15`, color }}>
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />{children}
  </span>
);

const SectionTitle = ({ overline, title, sub, center = false }) => (
  <Reveal>
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      {overline && <Tag>{overline}</Tag>}
      <h2 className="mt-5 font-display text-4xl lg:text-5xl font-bold leading-[1.08] tracking-tight" style={{ color: T.ink }}>{title}</h2>
      {sub && <p className="mt-5 text-lg leading-relaxed" style={{ color: T.inkSoft }}>{sub}</p>}
    </div>
  </Reveal>
);

const Card = ({ children, className = "", hover = true }) => (
  <div className={`bg-white rounded-3xl border border-[#E2EBE8] p-8 ${hover ? "transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,42,42,0.08)]" : ""} ${className}`}>
    {children}
  </div>
);

const PrimaryBtn = ({ to, href, children, large = false, testid }) => {
  const cls = `inline-flex items-center gap-2 ${large ? "px-7 py-4 text-base" : "px-5 py-3 text-sm"} rounded-full font-semibold text-white transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(14,127,118,0.3)] hover:shadow-[0_14px_40px_rgba(14,127,118,0.4)]`;
  const style = { background: `linear-gradient(135deg, ${T.brand}, ${T.brandDark})` };
  if (href) return <a href={href} className={cls} style={style} data-testid={testid}>{children}</a>;
  return <Link to={to} className={cls} style={style} data-testid={testid}>{children}</Link>;
};

const GhostBtn = ({ to, children, testid }) => (
  <Link to={to} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all hover:bg-[#F4F8F6]" style={{ color: T.ink, border: `1.5px solid ${T.line}` }} data-testid={testid}>
    {children}
  </Link>
);

/* ============================================================
 * 1. HOME
 * ============================================================ */
export function V2Home() {
  return (
    <V2Layout demoNote>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: `radial-gradient(ellipse 80% 50% at 20% 0%, ${T.brandSoft} 0%, transparent 60%), linear-gradient(180deg, ${T.bgSoft} 0%, white 100%)` }}>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(11,42,42,0.08) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="mx-auto max-w-7xl px-8 pt-24 pb-32 grid grid-cols-12 gap-12 items-center relative">
          <Reveal className="col-span-7">
            <Tag>Trusted by 1.2 lakh+ Surat families</Tag>
            <h1 className="mt-8 font-display text-7xl xl:text-[88px] font-bold leading-[0.96] tracking-tight" style={{ color: T.ink }}>
              Healthcare<br />that <span style={{ color: T.brand }}>feels personal.</span>
            </h1>
            <p className="mt-8 text-xl leading-relaxed max-w-xl" style={{ color: T.inkSoft }}>
              The Nirmal Privilege Card unlocks unlimited OPD consultations, family coverage, and pharmacy privileges — at Nirmal Super Speciality Hospital, Surat.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <PrimaryBtn to="/v2/plans" large testid="v2-hero-join">Explore membership <ArrowRight className="h-5 w-5" /></PrimaryBtn>
              <GhostBtn to="/v2/benefits" testid="v2-hero-benefits">See benefits</GhostBtn>
            </div>
            <div className="mt-14 flex items-center gap-10">
              {[["1.2L+", "Patients served"], ["30+", "Specialists"], ["20+", "Specialities"], ["24×7", "Ambulance"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-3xl font-bold" style={{ color: T.ink }}>{n}</div>
                  <div className="text-xs uppercase tracking-wider mt-1" style={{ color: T.inkSoft }}>{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal className="col-span-5" delay={0.2}>
            <div className="relative">
              <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-[0_30px_60px_rgba(11,42,42,0.18)]">
                <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1000&q=80" alt="Doctor" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -left-8 top-12 bg-white rounded-2xl p-5 shadow-[0_20px_40px_rgba(11,42,42,0.12)] w-64">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${T.brand}15` }}><HeartPulse className="h-5 w-5" style={{ color: T.brand }} /></div>
                  <div><div className="text-sm font-semibold" style={{ color: T.ink }}>Unlimited OPD</div><div className="text-xs" style={{ color: T.inkSoft }}>5 specialities included</div></div>
                </div>
              </div>
              <div className="absolute -right-6 bottom-20 bg-white rounded-2xl p-5 shadow-[0_20px_40px_rgba(11,42,42,0.12)] w-56">
                <div className="flex items-center gap-2"><Star className="h-4 w-4 fill-[#F5B945] text-[#F5B945]" /><Star className="h-4 w-4 fill-[#F5B945] text-[#F5B945]" /><Star className="h-4 w-4 fill-[#F5B945] text-[#F5B945]" /><Star className="h-4 w-4 fill-[#F5B945] text-[#F5B945]" /><Star className="h-4 w-4 fill-[#F5B945] text-[#F5B945]" /></div>
                <div className="mt-2 text-sm font-semibold" style={{ color: T.ink }}>4.9 / 5 average</div>
                <div className="text-xs" style={{ color: T.inkSoft }}>across 2,300+ reviews</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[#E2EBE8] py-10 bg-white">
        <div className="mx-auto max-w-7xl px-8 grid grid-cols-5 gap-8 items-center">
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: T.inkSoft }}>Recognised by</div>
          {["NABH-aligned", "ISO 9001:2015", "Indian Medical Assoc.", "FICCI Healthcare"].map((x) => (
            <div key={x} className="text-sm font-semibold opacity-50" style={{ color: T.ink }}>{x}</div>
          ))}
        </div>
      </section>

      {/* SPECIALITIES */}
      <section className="mx-auto max-w-7xl px-8 py-28">
        <div className="grid grid-cols-12 gap-12 items-end mb-16">
          <div className="col-span-7">
            <SectionTitle overline="Our specialities" title="Care across every life stage." sub="From paediatric vaccinations to cardiac care — your family's complete healthcare home." />
          </div>
          <div className="col-span-5 flex justify-end"><GhostBtn to="/v2/benefits">View all benefits <ArrowRight className="h-4 w-4" /></GhostBtn></div>
        </div>
        <div className="grid grid-cols-4 gap-6">
          {[
            { i: Stethoscope, t: "General Physician", d: "First-line consultation for every family member" },
            { i: Heart, t: "Cardiology", d: "Heart-health screenings and ongoing care" },
            { i: Activity, t: "Orthopedics", d: "Joint, bone and sports injury care" },
            { i: HeartPulse, t: "Gynecology", d: "Women's health across life stages" },
            { i: Users, t: "Pediatrics", d: "Compassionate paediatric care" },
            { i: Hospital, t: "General Surgery", d: "Minimally invasive procedures" },
            { i: Pill, t: "In-house Pharmacy", d: "Eligible pharmacy privileges" },
            { i: Syringe, t: "Diagnostics", d: "On-site lab + radiology with members' rates" },
          ].map((x, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <Card className="h-full group cursor-pointer">
                <div className="h-12 w-12 rounded-2xl grid place-items-center group-hover:scale-110 transition-transform" style={{ background: T.brandSoft, color: T.brand }}>
                  <x.i className="h-6 w-6" />
                </div>
                <div className="mt-6 font-display text-lg font-bold" style={{ color: T.ink }}>{x.t}</div>
                <div className="mt-2 text-sm" style={{ color: T.inkSoft }}>{x.d}</div>
              </Card>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PLANS PREVIEW */}
      <section className="bg-[#F4F8F6] py-28">
        <div className="mx-auto max-w-7xl px-8">
          <SectionTitle center overline="Membership plans" title="Choose the plan that fits your family." sub="Transparent pricing. No hidden costs. Same hospital, same doctors." />
          <div className="mt-16 grid grid-cols-3 gap-8">
            {[
              { name: "Bronze", price: "₹2,000", tag: "Individual", color: "#B8826E", seats: "1 member" },
              { name: "Silver", price: "₹3,500", tag: "Couple", color: "#7E8B96", seats: "2 members", featured: true },
              { name: "Gold", price: "₹5,500", tag: "Family", color: "#C89B3C", seats: "Couple + kids + parents" },
            ].map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className={`relative bg-white rounded-3xl p-10 transition-all hover:-translate-y-2 ${p.featured ? "border-2" : "border"}`}
                     style={{ borderColor: p.featured ? T.brand : T.line, boxShadow: p.featured ? "0 30px 60px rgba(14,127,118,0.15)" : "0 10px 30px rgba(11,42,42,0.04)" }}>
                  {p.featured && (
                    <div className="absolute -top-3 left-10 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: T.brand }}>MOST POPULAR</div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl" style={{ background: `${p.color}25` }} />
                    <div className="text-sm font-semibold uppercase tracking-wider" style={{ color: p.color }}>{p.name}</div>
                  </div>
                  <div className="mt-6 font-display text-5xl font-bold" style={{ color: T.ink }}>{p.price}<span className="text-base font-normal" style={{ color: T.inkSoft }}>/yr</span></div>
                  <div className="mt-1 text-sm" style={{ color: T.inkSoft }}>{p.seats} · incl. GST</div>
                  <div className="mt-8 space-y-3">
                    {["Unlimited OPD (5 specialities)", "Diagnostics privilege rates", "In-house pharmacy benefits", "Wellness initiatives access"].map((x) => (
                      <div key={x} className="flex items-center gap-3 text-sm" style={{ color: T.ink }}>
                        <Check className="h-4 w-4" style={{ color: T.brand }} /> {x}
                      </div>
                    ))}
                  </div>
                  <PrimaryBtn to="/v2/plans" testid={`v2-plan-${p.name}`}><span className="w-full text-center">Get {p.name}</span></PrimaryBtn>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <CTAStrip />

      {/* TESTIMONIAL PREVIEW */}
      <TestimonialSection />

      {/* FAQ PREVIEW */}
      <section className="mx-auto max-w-4xl px-8 py-28">
        <SectionTitle center overline="Quick answers" title="Common questions." />
        <div className="mt-12 space-y-4">
          {[
            ["Is NPC an insurance product?", "No. NPC is a healthcare privilege program — not insurance, mediclaim, or cashless. It complements existing insurance you may hold."],
            ["Can I add family members?", "Yes. The Silver covers a couple. Gold covers couple + children + parents under one card."],
            ["What's covered for unlimited OPD?", "General Physician, Orthopedics, Gynecology, Pediatrics, General Surgery — multiple visits during OPD hours."],
          ].map(([q, a], i) => <FAQItem key={i} q={q} a={a} />)}
        </div>
        <div className="mt-10 text-center"><GhostBtn to="/v2/faq">View all FAQs <ArrowRight className="h-4 w-4" /></GhostBtn></div>
      </section>
    </V2Layout>
  );
}

/* Shared CTA + Testimonial + FAQ used across pages */
function CTAStrip() {
  return (
    <section className="mx-auto max-w-7xl px-8 py-20">
      <div className="rounded-[2.5rem] p-16 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${T.brand}, ${T.brandDark})` }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="relative grid grid-cols-12 gap-10 items-center">
          <div className="col-span-8">
            <div className="text-sm uppercase tracking-[0.2em] text-white/70">Ready when you are</div>
            <h3 className="mt-4 font-display text-5xl font-bold text-white leading-tight">Activate your card<br />in under 5 minutes.</h3>
            <p className="mt-5 text-white/85 text-lg max-w-xl">Digital card with a unique QR is issued instantly. Show it at any Nirmal Hospital, Adajan reception.</p>
          </div>
          <div className="col-span-4 flex flex-col gap-3">
            <a href="#" className="px-7 py-4 rounded-full bg-white font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-all" style={{ color: T.brandDark }}>
              Join NPC now <ArrowRight className="h-4 w-4" />
            </a>
            <a href="tel:+916357033301" className="px-7 py-4 rounded-full border border-white/30 text-white font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-all">
              <Phone className="h-4 w-4" /> +91 63570 33301
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialSection() {
  const items = [
    { n: "Priyanka & Rohit Mehta", r: "Family of 4 · Adajan", q: "We've taken Mom for 6 OPD visits this year — zero hassle, just the QR code at reception. Worth every rupee.", c: "C89B3C" },
    { n: "Dr. Anand Bhatt", r: "Independent physician", q: "I recommend NPC to my patients with chronic conditions. The unlimited OPD model removes the 'do I really need to see the doctor today' hesitation.", c: "0E7F76" },
    { n: "Hema Tushar Doshi", r: "Senior citizen · Pal", q: "At 68, the pharmacy benefit is what I value most. The staff knows me by name. That's rare today.", c: "A53A2A" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-8 py-28">
      <SectionTitle overline="Member stories" title="What members say." sub="Voices from families across Surat who chose NPC as their healthcare home." />
      <div className="mt-16 grid grid-cols-3 gap-6">
        {items.map((x, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <Card className="h-full flex flex-col">
              <Quote className="h-7 w-7" style={{ color: T.brand, opacity: 0.5 }} />
              <p className="mt-6 text-base leading-relaxed flex-1" style={{ color: T.ink }}>{x.q}</p>
              <div className="mt-8 pt-6 border-t border-[#E2EBE8] flex items-center gap-3">
                <div className="h-11 w-11 rounded-full grid place-items-center text-white font-semibold text-sm" style={{ background: `#${x.c}` }}>{x.n.split(" ")[0][0]}{x.n.split(" ").slice(-1)[0][0]}</div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: T.ink }}>{x.n}</div>
                  <div className="text-xs" style={{ color: T.inkSoft }}>{x.r}</div>
                </div>
              </div>
            </Card>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FAQItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-[#E2EBE8] overflow-hidden transition-all hover:border-[#0E7F76]/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full px-7 py-6 flex items-center justify-between text-left">
        <span className="font-display text-lg font-semibold" style={{ color: T.ink }}>{q}</span>
        {open ? <ChevronUp className="h-5 w-5" style={{ color: T.brand }} /> : <ChevronDown className="h-5 w-5" style={{ color: T.inkSoft }} />}
      </button>
      {open && <div className="px-7 pb-6 text-base leading-relaxed" style={{ color: T.inkSoft }}>{a}</div>}
    </div>
  );
}

/* ============================================================
 * 2. PLANS
 * ============================================================ */
export function V2Plans() {
  return (
    <V2Layout demoNote>
      <PageHero overline="Membership" title="Plans built for real families." sub="No upsells. No surprise charges. Pick once, use all year." />
      <section className="mx-auto max-w-7xl px-8 py-20">
        <div className="grid grid-cols-3 gap-6">
          {[
            { name: "Bronze", price: "2,000", tag: "Individual", desc: "Single-member coverage, full benefits.", seats: 1 },
            { name: "Silver", price: "3,500", tag: "Couple", desc: "Two adults under one card.", seats: 2, featured: true },
            { name: "Gold", price: "5,500", tag: "Family", desc: "Couple + dependent children + parents.", seats: 6 },
          ].map((p, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className={`relative rounded-3xl p-10 ${p.featured ? "border-2 -translate-y-3" : "border"} bg-white`} style={{ borderColor: p.featured ? T.brand : T.line, boxShadow: p.featured ? "0 30px 60px rgba(14,127,118,0.15)" : "0 8px 24px rgba(11,42,42,0.04)" }}>
                {p.featured && <div className="absolute -top-3 left-10 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: T.brand }}>MOST POPULAR</div>}
                <div className="text-xs uppercase tracking-[0.2em]" style={{ color: T.inkSoft }}>{p.tag}</div>
                <h3 className="mt-3 font-display text-3xl font-bold" style={{ color: T.ink }}>{p.name}</h3>
                <div className="mt-6"><span className="font-display text-5xl font-bold" style={{ color: T.ink }}>₹{p.price}</span><span className="ml-1" style={{ color: T.inkSoft }}>/yr</span></div>
                <div className="mt-1 text-sm" style={{ color: T.inkSoft }}>incl. 18% GST · {p.seats} member{p.seats > 1 ? "s" : ""}</div>
                <p className="mt-6 text-sm leading-relaxed" style={{ color: T.inkSoft }}>{p.desc}</p>
                <div className="mt-8 space-y-3">
                  {["Unlimited OPD across 5 specialities", "Diagnostics privilege rates", "In-house pharmacy benefits", "Wellness initiatives access", "Digital card with unique QR", p.name === "Gold" && "Up to 6 family members covered"].filter(Boolean).map((x) => (
                    <div key={x} className="flex items-start gap-3 text-sm" style={{ color: T.ink }}>
                      <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: T.brand }} /> {x}
                    </div>
                  ))}
                </div>
                <div className="mt-10"><PrimaryBtn to="/v2/contact" testid={`v2-plans-cta-${p.name}`}><span className="flex-1 text-center">Get {p.name}</span> <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Comparison */}
        <Reveal className="mt-24">
          <SectionTitle center overline="Compare" title="What you get in each plan." />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 rounded-3xl border border-[#E2EBE8] overflow-hidden bg-white">
            <table className="w-full">
              <thead>
                <tr style={{ background: T.bgSoft }}>
                  <th className="text-left px-8 py-5 text-sm font-semibold" style={{ color: T.inkSoft }}>Feature</th>
                  {["Bronze", "Silver", "Gold"].map((x) => <th key={x} className="px-8 py-5 text-sm font-semibold" style={{ color: T.ink }}>{x}</th>)}
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ["Members covered", "1", "2", "Up to 6"],
                  ["Unlimited OPD (5 specs)", "✓", "✓", "✓"],
                  ["Diagnostics privilege rates", "✓", "✓", "✓"],
                  ["In-house pharmacy benefit", "✓", "✓", "✓"],
                  ["Parents covered", "—", "—", "✓"],
                  ["Children covered", "—", "—", "✓"],
                  ["Annual price (incl. GST)", "₹2,000", "₹3,500", "₹5,500"],
                ].map((row, i) => (
                  <tr key={i} className="border-t border-[#E2EBE8]">
                    <td className="px-8 py-5 font-medium" style={{ color: T.ink }}>{row[0]}</td>
                    {row.slice(1).map((c, j) => (
                      <td key={j} className="px-8 py-5 text-center" style={{ color: c === "✓" ? T.brand : c === "—" ? T.inkSoft : T.ink, fontWeight: c === "✓" ? 700 : 500 }}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </section>
      <CTAStrip />
    </V2Layout>
  );
}

/* ============================================================
 * 3. CORPORATE
 * ============================================================ */
export function V2Corporate() {
  return (
    <V2Layout demoNote>
      <PageHero overline="For your team" title="Corporate healthcare, made simple." sub="Onboard 10 or 10,000 employees on a single plan. GST-compliant invoicing. Bulk activation." />
      <section className="mx-auto max-w-7xl px-8 py-24 grid grid-cols-2 gap-16 items-center">
        <Reveal>
          <SectionTitle overline="Why companies pick NPC" title="Wellness, without the admin headache." />
          <div className="mt-10 space-y-6">
            {[
              [Building2, "Single GST invoice", "Consolidated billing with all employees on one corporate invoice — issued at order creation."],
              [Users, "Bulk activation", "Upload your employee CSV — every member gets a digital card and welcome credentials within seconds."],
              [BadgeCheck, "Transparent reporting", "Track activation, usage and renewals from your corporate dashboard."],
              [ShieldCheck, "Privacy guaranteed", "Employee health data stays between the doctor and the employee. No HR access."],
            ].map(([Icon, t, d], i) => (
              <div key={t} className="flex gap-5">
                <div className="h-12 w-12 flex-shrink-0 rounded-2xl grid place-items-center" style={{ background: T.brandSoft, color: T.brand }}><Icon className="h-6 w-6" /></div>
                <div>
                  <div className="font-display text-lg font-semibold" style={{ color: T.ink }}>{t}</div>
                  <div className="mt-1 text-sm leading-relaxed" style={{ color: T.inkSoft }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="rounded-3xl p-10" style={{ background: `linear-gradient(135deg, ${T.brand}, ${T.brandDark})` }}>
            <div className="text-xs uppercase tracking-[0.2em] text-white/65">Quick onboarding</div>
            <h3 className="mt-4 font-display text-3xl font-bold text-white">4 steps. One afternoon.</h3>
            <ol className="mt-10 space-y-6 text-white/90">
              {[
                "Share employee list (name, email, mobile, Aadhaar)",
                "Receive GST invoice + payment link",
                "Pay via bank transfer or online",
                "Cards activate instantly · welcome emails fire automatically",
              ].map((x, i) => (
                <li key={x} className="flex gap-5">
                  <div className="h-9 w-9 rounded-full bg-white text-[#0A5C56] grid place-items-center font-bold text-sm">{i + 1}</div>
                  <div className="text-base leading-relaxed pt-1">{x}</div>
                </li>
              ))}
            </ol>
            <div className="mt-10">
              <a href="mailto:corporate@npc.koderclub.co.in" className="px-6 py-3 rounded-full bg-white font-semibold inline-flex items-center gap-2" style={{ color: T.brandDark }}>Get a corporate quote <ArrowUpRight className="h-4 w-4" /></a>
            </div>
          </div>
        </Reveal>
      </section>
      <CTAStrip />
    </V2Layout>
  );
}

/* ============================================================
 * 4. ABOUT
 * ============================================================ */
export function V2About() {
  return (
    <V2Layout demoNote>
      <PageHero overline="About us" title="A hospital with a difference." sub="At Nirmal Super Speciality Hospital, healthcare goes beyond infrastructure — it's built on compassion, trust, and timely medical care." />
      <section className="mx-auto max-w-7xl px-8 py-24 grid grid-cols-2 gap-12">
        <Reveal>
          <Card>
            <div className="h-12 w-12 rounded-2xl grid place-items-center" style={{ background: T.brandSoft, color: T.brand }}><BadgeCheck className="h-6 w-6" /></div>
            <h3 className="mt-6 font-display text-2xl font-bold" style={{ color: T.ink }}>Vision Statement</h3>
            <p className="mt-4 font-display text-xl leading-snug" style={{ color: T.ink }}>To provide <span style={{ color: T.brand }}>advance healthcare services</span> with quality care on a foundation of integrity and compassion.</p>
          </Card>
        </Reveal>
        <Reveal delay={0.1}>
          <Card>
            <div className="h-12 w-12 rounded-2xl grid place-items-center" style={{ background: `${T.accent}25`, color: "#8B6510" }}><Sparkles className="h-6 w-6" /></div>
            <h3 className="mt-6 font-display text-2xl font-bold" style={{ color: T.ink }}>Mission Statement</h3>
            <p className="mt-4 font-display text-xl leading-snug" style={{ color: T.ink }}>At Nirmal Hospital, our mission is to deliver <span style={{ color: T.brand }}>trusted, patient-centric healthcare services</span> with unwavering integrity and compassion.</p>
          </Card>
        </Reveal>
      </section>

      <section className="bg-[#F4F8F6] py-24">
        <div className="mx-auto max-w-7xl px-8">
          <SectionTitle center overline="We strive to" title="The three pursuits that shape every decision." />
          <div className="mt-16 grid grid-cols-3 gap-6">
            {[
              ["Quality healthcare to the community", "Every patient interaction held to a single standard — the one we'd want for our own family."],
              ["A premier academic institute in healthcare", "Mentoring the next generation of clinicians while staying at the cutting edge of medicine."],
              ["A culture of learning and innovation", "Continuous training, peer review, and technology adoption — built into the daily rhythm."],
            ].map(([t, d], i) => (
              <Reveal key={i} delay={i * 0.1}>
                <Card className="h-full">
                  <div className="font-display text-5xl font-bold" style={{ color: T.brand }}>{String(i + 1).padStart(2, "0")}</div>
                  <h4 className="mt-6 font-display text-xl font-semibold" style={{ color: T.ink }}>{t}</h4>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: T.inkSoft }}>{d}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Doctors */}
      <section className="mx-auto max-w-7xl px-8 py-24">
        <SectionTitle overline="Our specialists" title="Doctors who listen, and lead." sub="A small sample of our 30+ consulting specialists." />
        <div className="mt-12 grid grid-cols-4 gap-6">
          {[
            ["Dr. Naresh Mehta", "Cardiology", "20+ yrs"],
            ["Dr. Shivangi Choraria", "Pediatrics", "15+ yrs"],
            ["Dr. Bhushan Dodia", "Orthopedics", "18+ yrs"],
            ["Dr. Jagruti Desai", "Gynecology", "22+ yrs"],
          ].map((d, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="rounded-3xl overflow-hidden border border-[#E2EBE8] bg-white group">
                <div className="aspect-square overflow-hidden" style={{ background: T.bgSoft }}>
                  <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(d[0].replace("Dr. ", ""))}&background=0E7F76&color=FFFFFF&bold=true&size=400&font-size=0.36`} alt={d[0]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="font-display text-base font-semibold" style={{ color: T.ink }}>{d[0]}</div>
                  <div className="mt-1 text-xs uppercase tracking-wider" style={{ color: T.brand }}>{d[1]} · {d[2]}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <CTAStrip />
    </V2Layout>
  );
}

/* ============================================================
 * 5. BENEFITS
 * ============================================================ */
export function V2Benefits() {
  return (
    <V2Layout demoNote>
      <PageHero overline="Membership benefits" title="Six privileges. One card." sub="Built for real Indian family healthcare needs — across age and life stage." />
      <section className="mx-auto max-w-7xl px-8 py-24">
        <div className="grid grid-cols-3 gap-6">
          {[
            [Stethoscope, "Unlimited OPD", "Consult eligible specialists during OPD hours — Physician, Orthopedics, Gynecology, Pediatrics, General Surgery."],
            [HeartPulse, "Preventive care", "Special rates on diagnostics, pathology, radiology and preventive health-check packages."],
            [Users, "Family ecosystem", "Couple, children, parents — all on a single membership."],
            [Pill, "Pharmacy privileges", "Eligible discounts on in-house pharmacy purchases. Walk in, save money."],
            [Sparkles, "Wellness initiatives", "Health awareness programs, community workshops and webinars across the year."],
            [ShieldCheck, "Long-term relationship", "Continuous care under one program — built for lifetime, not transactions."],
          ].map(([Icon, t, d], i) => (
            <Reveal key={i} delay={i * 0.05}>
              <Card className="h-full">
                <div className="h-14 w-14 rounded-2xl grid place-items-center" style={{ background: T.brandSoft, color: T.brand }}><Icon className="h-7 w-7" /></div>
                <h3 className="mt-7 font-display text-xl font-bold" style={{ color: T.ink }}>{t}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: T.inkSoft }}>{d}</p>
              </Card>
            </Reveal>
          ))}
        </div>

        {/* Brochure */}
        <Reveal className="mt-20">
          <div className="rounded-3xl p-12 grid grid-cols-12 gap-10 items-center" style={{ background: T.bgSoft }}>
            <div className="col-span-7">
              <Tag>Detailed brochure</Tag>
              <h3 className="mt-5 font-display text-4xl font-bold" style={{ color: T.ink }}>Read every detail. Take it home.</h3>
              <p className="mt-4 text-base leading-relaxed max-w-lg" style={{ color: T.inkSoft }}>The 3-fold English membership brochure — plans, benefits, exclusions and FAQs in one printable document.</p>
            </div>
            <div className="col-span-5">
              <div className="bg-white rounded-2xl border border-[#E2EBE8] p-7 shadow-[0_15px_40px_rgba(11,42,42,0.08)]">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-2xl grid place-items-center" style={{ background: T.brandSoft, color: T.brand }}><FileText className="h-7 w-7" /></div>
                  <div className="flex-1">
                    <div className="font-display text-lg font-semibold" style={{ color: T.ink }}>Membership Brochure (English)</div>
                    <div className="text-xs mt-1" style={{ color: T.inkSoft }}>PDF · ~20 MB · 3-fold layout</div>
                  </div>
                </div>
                <a href="https://pub-fd8409bf8a9247aebf3fdc6d6d5af80f.r2.dev/brochures/npc-membership-english-v1.pdf" target="_blank" rel="noopener noreferrer" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white transition-all hover:-translate-y-0.5" style={{ background: T.brand }}>
                  Download brochure <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
      <CTAStrip />
    </V2Layout>
  );
}

/* ============================================================
 * 6. WHY US
 * ============================================================ */
export function V2WhyUs() {
  return (
    <V2Layout demoNote>
      <PageHero overline="Why Nirmal" title="Six reasons families choose us." />
      <section className="mx-auto max-w-7xl px-8 py-24 grid grid-cols-2 gap-6">
        {[
          [Hospital, "20+ specialities, one campus", "From paediatrics to cardiac care — under one roof in Adajan."],
          [Users, "30+ specialist doctors", "Senior consultants with 15–25 years of clinical experience."],
          [Clock3, "24×7 ambulance & emergency", "Round-the-clock response. No questions asked."],
          [Award, "NABH-aligned protocols", "Patient safety standards across every department."],
          [HeartPulse, "1.2 lakh+ patients served", "Trusted by families across Adajan, Pal, Rander, Palanpur Patia."],
          [Sparkles, "Membership built around you", "Unlimited OPD, family coverage, transparent pricing."],
        ].map(([Icon, t, d], i) => (
          <Reveal key={i} delay={i * 0.06}>
            <Card className="h-full flex gap-6">
              <div className="h-14 w-14 flex-shrink-0 rounded-2xl grid place-items-center" style={{ background: T.brandSoft, color: T.brand }}><Icon className="h-7 w-7" /></div>
              <div>
                <h3 className="font-display text-xl font-bold" style={{ color: T.ink }}>{t}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: T.inkSoft }}>{d}</p>
              </div>
            </Card>
          </Reveal>
        ))}
      </section>
      <CTAStrip />
    </V2Layout>
  );
}

/* ============================================================
 * 7. FAQ
 * ============================================================ */
export function V2FAQ() {
  const faqs = [
    ["What is the Nirmal Privilege Card (NPC)?", "NPC is a healthcare privilege and patient support program offered by Nirmal Group of Hospitals. It provides eligible members and families with easier access to unlimited OP consultations (selected specialities), awareness programs, and selected hospital benefits."],
    ["Is NPC a health insurance product?", "No. NPC is not insurance, mediclaim, cashless scheme, or financial instrument. It is a privilege program that complements your existing insurance."],
    ["Who can enroll in NPC?", "Individuals, couples, families, and organisations may enroll under available membership plans, subject to verification and approval."],
    ["What plans are available?", "Bronze (Individual), Silver (Couple), and Gold (Couple + Children + Parents). Pricing and structure subject to hospital policy."],
    ["What are the key benefits?", "Unlimited OPD with 5 eligible specialities; special rates on diagnostics; pharmacy privileges on in-house purchases; access to wellness initiatives; family healthcare ecosystem."],
    ["Which specialities are covered?", "General Physician, Orthopedics, Gynecology, Pediatrics, General Surgery — multiple visits during OPD hours."],
    ["Are emergency services covered?", "No. NPC benefits are not applicable during emergency services or emergency admissions."],
    ["Public holiday consultations?", "Not applicable unless specifically approved by hospital management."],
    ["Priority admission or emergency treatment?", "No. Membership does not guarantee admission, treatment, emergency priority, bed availability, ICU availability, or doctor availability."],
    ["Diagnostics and pathology?", "Members receive special rates on selected investigations performed within hospital infrastructure."],
    ["Medicines?", "Eligible benefits apply only on purchases from the in-house hospital pharmacy."],
    ["IPD bill discount if I purchase during stay?", "To avail an IPD bill discount, the NPC card must be active and issued PRIOR to the admission date. Purchases made during a hospital stay apply only from discharge onwards."],
    ["Can NPC be combined with insurance or other offers?", "No. NPC benefits cannot be combined with insurance claims, TPA billing, government schemes, discounted packages, or other promotional offers."],
    ["What is NOT included?", "Implants and stents, blood products, outsourced investigations, government taxes/GST, special promotional packages, and services already under discounted pricing."],
    ["Can pricing or structure change?", "Yes. Hospital management reserves the right to revise, modify, suspend, or discontinue any plan, benefit, pricing, or policy without prior notice."],
    ["Can membership be terminated?", "Misuse, fraudulent activity, policy violations, misconduct, or non-compliance may lead to suspension or cancellation."],
    ["Is NPC transferable?", "No. Membership is non-transferable and can only be used by registered eligible members under the enrolled plan."],
  ];
  return (
    <V2Layout demoNote>
      <PageHero overline="Frequently asked" title="Questions, answered." sub="If you don't find what you're looking for, our team is one call away." />
      <section className="mx-auto max-w-4xl px-8 py-20">
        <div className="space-y-3">
          {faqs.map(([q, a], i) => <FAQItem key={i} q={q} a={a} defaultOpen={i === 0} />)}
        </div>
        <Reveal className="mt-16 text-center">
          <p className="text-sm" style={{ color: T.inkSoft }}>Still have questions?</p>
          <div className="mt-4"><PrimaryBtn to="/v2/contact" testid="v2-faq-cta">Talk to our team <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
        </Reveal>
      </section>
    </V2Layout>
  );
}

/* ============================================================
 * 8. TESTIMONIALS
 * ============================================================ */
export function V2Testimonials() {
  return (
    <V2Layout demoNote>
      <PageHero overline="Member voices" title="Real families. Real outcomes." sub="The reason we wake up every morning." />
      <TestimonialSection />
      <section className="mx-auto max-w-7xl px-8 pb-24 grid grid-cols-2 gap-6">
        {[
          ["The OPD experience is dignified — no long wait, no rushed consult. NPC made primary healthcare actually pleasant.", "Dr. Mansi Patel", "Family of 3 · Pal"],
          ["My father needed 4 cardiology follow-ups this quarter alone. With NPC, no decision-making about whether the visit is 'worth it'.", "Bhavin Trivedi", "Care-giver · Adajan"],
          ["The brochure is honest about what's NOT covered — that built trust before the first rupee was spent.", "Anjali R.", "Member since 2025"],
          ["Booked the gold plan for parents and us. The pharmacy savings alone covered the membership fee.", "Yashodhan Joshi", "Member · Honey Park"],
        ].map(([q, n, r], i) => (
          <Reveal key={i} delay={i * 0.05}>
            <Card>
              <Quote className="h-7 w-7" style={{ color: T.brand, opacity: 0.5 }} />
              <p className="mt-5 text-base leading-relaxed" style={{ color: T.ink }}>{q}</p>
              <div className="mt-6 text-sm font-semibold" style={{ color: T.ink }}>{n}</div>
              <div className="text-xs" style={{ color: T.inkSoft }}>{r}</div>
            </Card>
          </Reveal>
        ))}
      </section>
    </V2Layout>
  );
}

/* ============================================================
 * 9. CONTACT
 * ============================================================ */
export function V2Contact() {
  return (
    <V2Layout demoNote>
      <PageHero overline="Get in touch" title="Talk to a human. Today." sub="We're at the hospital reception from 8 AM to 8 PM, every day." />
      <section className="mx-auto max-w-7xl px-8 py-20 grid grid-cols-12 gap-10">
        <Reveal className="col-span-5 space-y-6">
          {[
            [Phone, "Call us", "+91 63570 33301", "Privilege Card Helpline · 24×7"],
            [Mail, "Email", "support@npc.nirmalhospitals.in", "Replies within 6 working hours"],
            [MapPin, "Visit", "Adajan Gam, L. P. Savani", "Surat — 394510, Gujarat"],
            [Clock3, "Hours", "Mon–Sun · 8 AM – 8 PM", "Emergency 24×7"],
          ].map(([Icon, t, primary, secondary], i) => (
            <Reveal key={i} delay={i * 0.05}>
              <Card className="flex gap-5">
                <div className="h-12 w-12 flex-shrink-0 rounded-2xl grid place-items-center" style={{ background: T.brandSoft, color: T.brand }}><Icon className="h-6 w-6" /></div>
                <div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: T.inkSoft }}>{t}</div>
                  <div className="mt-1 font-display text-lg font-semibold" style={{ color: T.ink }}>{primary}</div>
                  <div className="mt-0.5 text-sm" style={{ color: T.inkSoft }}>{secondary}</div>
                </div>
              </Card>
            </Reveal>
          ))}
        </Reveal>
        <Reveal className="col-span-7" delay={0.15}>
          <Card hover={false}>
            <h3 className="font-display text-2xl font-bold" style={{ color: T.ink }}>Send us a message</h3>
            <p className="mt-2 text-sm" style={{ color: T.inkSoft }}>Tell us about your healthcare needs — our membership team will respond within a working day.</p>
            <div className="mt-7 grid grid-cols-2 gap-4">
              {[["Name", "text"], ["Email", "email"], ["Mobile", "tel"], ["Plan of interest", "text"]].map(([label, type]) => (
                <div key={label}>
                  <label className="text-xs uppercase tracking-wider font-semibold" style={{ color: T.inkSoft }}>{label}</label>
                  <input type={type} className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E2EBE8] text-sm focus:border-[#0E7F76] focus:outline-none transition-colors" placeholder={label} />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-wider font-semibold" style={{ color: T.inkSoft }}>How can we help?</label>
              <textarea rows={4} className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E2EBE8] text-sm focus:border-[#0E7F76] focus:outline-none transition-colors resize-none" placeholder="Tell us about your family and what you're looking for…" />
            </div>
            <div className="mt-6"><PrimaryBtn href="#" large testid="v2-contact-submit">Send message <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
          </Card>
        </Reveal>
      </section>

      {/* Map placeholder */}
      <section className="mx-auto max-w-7xl px-8 pb-24">
        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-[#E2EBE8] aspect-[21/9] relative" style={{ background: T.bgSoft }}>
            <img src="https://maps.googleapis.com/maps/api/staticmap?center=21.1956,72.7969&zoom=15&size=1400x600&maptype=roadmap" alt="Map" className="w-full h-full object-cover opacity-90" onError={(e) => { e.target.style.display = "none"; }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_rgba(11,42,42,0.15)] text-center">
                <MapPin className="h-7 w-7 mx-auto" style={{ color: T.brand }} />
                <div className="mt-3 font-display text-lg font-bold" style={{ color: T.ink }}>Nirmal Super Speciality Hospital</div>
                <div className="text-sm" style={{ color: T.inkSoft }}>Adajan Gam, L. P. Savani, Surat — 394510</div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </V2Layout>
  );
}

/* ============================================================
 * 10–11. LEGAL (Privacy + Terms)
 * ============================================================ */
function LegalLayout({ title, sub, sections }) {
  return (
    <V2Layout demoNote>
      <PageHero overline="Legal" title={title} sub={sub} />
      <section className="mx-auto max-w-3xl px-8 py-20">
        <div className="space-y-10">
          {sections.map(([heading, body], i) => (
            <Reveal key={i} delay={i * 0.03}>
              <h3 className="font-display text-xl font-bold" style={{ color: T.ink }}>{heading}</h3>
              <p className="mt-3 text-base leading-relaxed" style={{ color: T.inkSoft }}>{body}</p>
            </Reveal>
          ))}
        </div>
      </section>
    </V2Layout>
  );
}

export function V2Privacy() {
  return <LegalLayout
    title="Privacy policy" sub="How we collect, use, and protect your information."
    sections={[
      ["1. Information we collect", "When you join NPC, we collect name, contact details, Aadhaar (for KYC), date of birth, and family member information you choose to share. Hospital records (consultation notes, diagnostics) are owned by you and the treating physician."],
      ["2. How we use your information", "To issue and verify your membership, send invoices and welcome credentials, and contact you about renewals or service updates. We do not sell your data."],
      ["3. Data security", "Encrypted at rest and in transit. Access is limited to authorised hospital staff on a need-to-know basis. Industry-standard practices (TLS, hashed passwords, access logs)."],
      ["4. Third-party services", "We use Razorpay for payments, Resend for emails, and Cloudflare R2 for document storage. Each is bound by their own privacy and data-protection terms."],
      ["5. Your rights", "Request access, correction, or deletion of your personal data by writing to support@npc.nirmalhospitals.in. Members may also self-serve from the customer dashboard."],
      ["6. Updates", "We may revise this policy. Material changes will be notified via email and dashboard banner."],
    ]}
  />;
}

export function V2Terms() {
  return <LegalLayout
    title="Terms of service" sub="The rules that govern your use of the NPC membership program."
    sections={[
      ["1. Membership scope", "NPC is a healthcare privilege program — not insurance, mediclaim, or any financial product. Benefits are subject to hospital policy and may be revised without prior notice."],
      ["2. Eligibility", "Membership is open to individuals, couples, families, and organisations subject to verification and approval. Misuse or fraud may lead to cancellation."],
      ["3. Pricing & GST", "All listed prices are inclusive of 18% GST. The hospital reserves the right to revise pricing at any time."],
      ["4. Refunds", "Refunds are processed on a case-by-case basis. Activated digital cards with usage are not refundable."],
      ["5. Use of benefits", "Benefits cannot be combined with insurance claims, TPA billing, government schemes, discounted packages, or other promotional offers."],
      ["6. Liability", "The hospital is not responsible for guaranteed admission, bed availability, doctor availability, or any specific clinical outcome."],
      ["7. Governing law", "These terms are governed by the laws of India and subject to the jurisdiction of courts in Surat, Gujarat."],
    ]}
  />;
}

/* ============================================================
 * Shared page hero for inner pages
 * ============================================================ */
function PageHero({ overline, title, sub }) {
  return (
    <section className="relative overflow-hidden" style={{ background: `radial-gradient(ellipse 60% 50% at 30% 0%, ${T.brandSoft} 0%, transparent 60%), linear-gradient(180deg, ${T.bgSoft} 0%, white 100%)` }}>
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(11,42,42,0.08) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
      <div className="mx-auto max-w-7xl px-8 pt-20 pb-28 relative">
        <Reveal>
          <Tag>{overline}</Tag>
          <h1 className="mt-7 font-display text-6xl lg:text-7xl font-bold leading-[1.0] tracking-tight max-w-3xl" style={{ color: T.ink }}>{title}</h1>
          {sub && <p className="mt-7 text-xl leading-relaxed max-w-2xl" style={{ color: T.inkSoft }}>{sub}</p>}
        </Reveal>
      </div>
    </section>
  );
}
